import React, { useState, useEffect } from 'react';
import { SavedTemplate, VdpTemplate, VdpLayout, Unit } from '../../types';
import { 
  getSavedTemplates, 
  saveUserTemplate, 
  deleteUserTemplate, 
  exportTemplateAsJSON 
} from '../../utils/presetTemplates';
import { 
  X, Save, FolderOpen, Download, Upload, Trash2, Copy, Sparkles, Check, Search, Layout, FileText, Plus
} from 'lucide-react';
import { MacWindow } from './MacWindow';

interface TemplateManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTemplate: VdpTemplate;
  currentLayout: VdpLayout;
  currentUnit: Unit;
  onLoadTemplate: (template: VdpTemplate, layout?: VdpLayout, unit?: Unit) => void;
  initialShowSaveForm?: boolean;
}

export const TemplateManagerModal: React.FC<TemplateManagerModalProps> = ({
  isOpen,
  onClose,
  currentTemplate,
  currentLayout,
  currentUnit,
  onLoadTemplate,
  initialShowSaveForm = false
}) => {
  const [templates, setTemplates] = useState<SavedTemplate[]>([]);
  React.useEffect(() => {
    getSavedTemplates()
      .then(setTemplates)
      .catch((err) => {
        console.error("Failed to load templates on mount:", err);
      });
  }, []);
  const [activeTab, setActiveTab] = useState<'all' | 'user' | 'presets'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Save form state
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');
  const [includeLayout, setIncludeLayout] = useState(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadTemplateList();
      if (initialShowSaveForm) {
        setNewTemplateName(`Design ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
        setShowSaveForm(true);
      }
    }
  }, [isOpen, initialShowSaveForm]);

  const loadTemplateList = async () => {
    try {
      const list = await getSavedTemplates();
      setTemplates(list);
    } catch (err) {
      console.error("Failed to load template list:", err);
    }
  };

  const showSuccessToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleSaveCurrent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTemplateName.trim()) return;

    try {
      const saved = await saveUserTemplate(
        newTemplateName,
        currentTemplate,
        includeLayout ? currentLayout : undefined,
        currentUnit,
        newTemplateDesc
      );

      await loadTemplateList();
      setShowSaveForm(false);
      setNewTemplateName('');
      setNewTemplateDesc('');
      setActiveTab('user');
      showSuccessToast(`Template "${saved.name}" saved successfully!`);
    } catch (err: any) {
      showSuccessToast(`Failed to save template: ${err?.message || 'Database error'}`);
    }
  };

  const handleLoad = (tpl: SavedTemplate) => {
    onLoadTemplate(tpl.template, tpl.layout, tpl.appUnit);
    showSuccessToast(`Loaded template "${tpl.name}"`);
    onClose();
  };

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string, name: string) => {
    if (deletingId !== id) {
      setDeletingId(id);
      setTimeout(() => {
        setDeletingId((current) => (current === id ? null : current));
      }, 4000);
      return;
    }

    setDeletingId(null);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    try {
      await deleteUserTemplate(id);
      await loadTemplateList();
      showSuccessToast(`Template "${name}" deleted.`);
    } catch (err: any) {
      console.error("Failed to delete template:", err);
      showSuccessToast(`Error deleting template: ${err?.message || 'Database error'}`);
    }
  };

  const handleDuplicate = async (tpl: SavedTemplate) => {
    const dupName = `${tpl.name} (Copy)`;
    try {
      await saveUserTemplate(dupName, tpl.template, tpl.layout, tpl.appUnit, `Duplicate of ${tpl.name}`);
      await loadTemplateList();
      setActiveTab('user');
      showSuccessToast(`Duplicated "${dupName}"`);
    } catch (err: any) {
      console.error("Failed to duplicate template:", err);
      showSuccessToast(`Error duplicating template: ${err?.message || 'Database error'}`);
    }
  };

  const handleImportJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        if (!json.template || !Array.isArray(json.template.elements)) {
          showSuccessToast('Invalid template format. Must contain valid VDP template data.');
          return;
        }

        const imported = await saveUserTemplate(
          json.name || file.name.replace('.json', ''),
          json.template,
          json.layout,
          json.appUnit || currentUnit,
          json.description || 'Imported from JSON file'
        );

        loadTemplateList();
        showSuccessToast(`Imported template "${imported.name}"`);
      } catch (err) {
        showSuccessToast('Could not parse JSON file. Please check file validity.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  if (!isOpen) return null;

  const filteredTemplates = templates.filter(t => {
    if (activeTab === 'user' && t.isPreset) return false;
    if (activeTab === 'presets' && !t.isPreset) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        t.name.toLowerCase().includes(q) ||
        (t.description && t.description.toLowerCase().includes(q))
      );
    }
    return true;
  });

  return (
    <MacWindow isOpen={isOpen} onClose={onClose} title="Template Manager & Library">
      <div className="flex flex-col w-full h-full overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between bg-zinc-950">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#1473E6] text-white flex items-center justify-center shadow-md">
              <FolderOpen className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-zinc-200">Template Manager & Library</h2>
              <p className="text-xs text-zinc-500">Save, reuse, import & export your sticker and label design states</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setNewTemplateName(`My Design (${currentTemplate.width}x${currentTemplate.height} ${currentUnit})`);
                setShowSaveForm(true);
              }}
              className="px-3.5 py-2 bg-[#1473E6] hover:bg-blue-700 text-white text-xs font-semibold rounded-md shadow-sm flex items-center gap-1.5 transition-colors"
            >
              <Save className="w-4 h-4" /> Save Current Design
            </button>
            <button 
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-zinc-400 rounded-md hover:bg-zinc-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Success Toast */}
        {toastMessage && (
          <div className="bg-[#1473E6] text-white text-xs font-semibold px-4 py-2.5 flex items-center justify-between shadow-inner">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4" /> {toastMessage}
            </span>
          </div>
        )}

        {/* Save Current Design Dropdown Form */}
        {showSaveForm && (
          <form onSubmit={handleSaveCurrent} className="p-4 bg-zinc-950 border-b border-zinc-800 animate-in slide-in-from-top-2 duration-150">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-blue-400 flex items-center gap-2">
                <Plus className="w-4 h-4 text-blue-500" /> Save Current Canvas & Settings as Template
              </h3>
              <button type="button" onClick={() => setShowSaveForm(false)} className="text-blue-400 hover:text-blue-300 text-xs">
                Cancel
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-semibold text-zinc-300 mb-1 block">Template Name *</label>
                <input 
                  type="text"
                  required
                  placeholder="e.g. Asset Label 2x1"
                  className="w-full px-3 py-1.5 border border-zinc-700 rounded-md text-xs bg-zinc-900 focus:ring-2 focus:ring-blue-600 outline-none"
                  value={newTemplateName}
                  onChange={e => setNewTemplateName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-300 mb-1 block">Description (Optional)</label>
                <input 
                  type="text"
                  placeholder="e.g. For inventory tagging with QR and barcode"
                  className="w-full px-3 py-1.5 border border-zinc-700 rounded-md text-xs bg-zinc-900 focus:ring-2 focus:ring-blue-600 outline-none"
                  value={newTemplateDesc}
                  onChange={e => setNewTemplateDesc(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={includeLayout} 
                  onChange={e => setIncludeLayout(e.target.checked)} 
                  className="text-blue-600 rounded"
                />
                <span>Include current print grid layout settings (page size, rows, margins)</span>
              </label>
              <button 
                type="submit"
                className="px-4 py-1.5 bg-[#1473E6] hover:bg-blue-700 text-white text-xs font-semibold rounded-md shadow-sm flex items-center gap-1.5"
              >
                <Save className="w-3.5 h-3.5" /> Save Template
              </button>
            </div>
          </form>
        )}

        {/* Toolbar & Filters */}
        <div className="px-5 py-3 border-b border-zinc-800 bg-zinc-900 flex flex-wrap items-center justify-between gap-3">
          {/* Tabs */}
          <div className="flex items-center gap-1 bg-zinc-800 p-1 rounded-md">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === 'all' ? 'bg-zinc-900 text-zinc-200 shadow-xs' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              All Templates ({templates.length})
            </button>
            <button
              onClick={() => setActiveTab('user')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === 'user' ? 'bg-zinc-900 text-zinc-200 shadow-xs' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              My Saved ({templates.filter(t => !t.isPreset).length})
            </button>
            <button
              onClick={() => setActiveTab('presets')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === 'presets' ? 'bg-zinc-900 text-zinc-200 shadow-xs' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              Built-in Presets ({templates.filter(t => t.isPreset).length})
            </button>
          </div>

          {/* Search & Actions */}
          <div className="flex items-center gap-2 flex-1 max-w-xs">
            <div className="relative w-full">
              <Search className="w-4 h-4 text-zinc-400 absolute left-2.5 top-2.5" />
              <input 
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-md text-xs focus:ring-2 focus:ring-blue-600 outline-none"
              />
            </div>
            <label className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold rounded-md border border-zinc-800 cursor-pointer flex items-center gap-1.5 shrink-0 transition-colors" title="Import template JSON file">
              <Upload className="w-3.5 h-3.5" />
              <span>Import</span>
              <input type="file" accept=".json" onChange={handleImportJSON} className="hidden" />
            </label>
          </div>
        </div>

        {/* Templates Grid Container */}
        <div className="p-5 overflow-y-auto flex-1 bg-zinc-950">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 flex flex-col items-center">
              <FolderOpen className="w-12 h-12 text-zinc-300 mb-2" />
              <p className="text-sm font-medium text-zinc-400">No templates found</p>
              <p className="text-xs text-zinc-400 mt-1">Try resetting your search or save your current design state as a template.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredTemplates.map((tpl) => (
                <div 
                  key={tpl.id}
                  className="bg-zinc-900 rounded-md border border-zinc-800 p-4 shadow-xs hover:shadow-md hover:border-orange-400 transition-all flex flex-col justify-between group"
                >
                  <div>
                    {/* Header line */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-zinc-200 text-sm group-hover:text-blue-600 transition-colors">{tpl.name}</h4>
                          {tpl.isPreset ? (
                            <span className="bg-indigo-950/40 text-indigo-300 border border-indigo-900/40 text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-indigo-400" /> Preset
                            </span>
                          ) : (
                            <span className="bg-emerald-950/40 text-emerald-300 border border-emerald-900/40 text-[10px] font-bold px-1.5 py-0.5 rounded">
                              Saved
                            </span>
                          )}
                        </div>
                        {tpl.description && (
                          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{tpl.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Micro Canvas Preview & Spec */}
                    <div className="my-3 bg-zinc-800 rounded-md p-2.5 border border-zinc-800 flex gap-3 items-center">
                      <div className="w-24 h-16 bg-zinc-900 border border-zinc-700 rounded shadow-2xs relative flex items-center justify-center overflow-hidden shrink-0">
                        {/* Render miniature SVG representation */}
                        <svg className="w-full h-full" viewBox={`0 0 ${tpl.template.width || 200} ${tpl.template.height || 100}`}>
                          <rect width="100%" height="100%" fill={tpl.template.backgroundColor || "#ffffff"} />
                          {tpl.template.elements.map((el, i) => {
                            if (el.type === 'rectangle' || el.type === 'circle') {
                              return (
                                <rect 
                                  key={i} 
                                  x={el.x} 
                                  y={el.y} 
                                  width={el.width || 40} 
                                  height={el.height || 20} 
                                  fill={el.color || 'transparent'} 
                                  stroke={el.borderColor || '#3b82f6'} 
                                  strokeWidth={el.borderWidth || 1} 
                                  rx={el.type === 'circle' ? 10 : (el.borderRadius || 0)}
                                />
                              );
                            }
                            if (el.type === 'barcode' || el.type === 'ean13') {
                              return (
                                <rect key={i} x={el.x} y={el.y} width={el.width || 50} height={el.height || 15} fill="#334155" opacity={0.6} />
                              );
                            }
                            if (el.type === 'qr') {
                              return (
                                <rect key={i} x={el.x} y={el.y} width={el.width || 20} height={el.height || 20} fill="#1e293b" />
                              );
                            }
                            return (
                              <rect key={i} x={el.x} y={el.y} width={el.width || 40} height={8} fill={el.color || "#0284c7"} opacity={0.7} />
                            );
                          })}
                        </svg>
                      </div>

                      <div className="text-xs text-zinc-400 space-y-1 font-sans">
                        <div className="flex items-center gap-1.5 font-medium">
                          <Layout className="w-3.5 h-3.5 text-zinc-400" />
                          <span>Size: {tpl.template.width} × {tpl.template.height} {tpl.appUnit || 'pt'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-zinc-500">
                          <FileText className="w-3.5 h-3.5 text-zinc-400" />
                          <span>Elements: {tpl.template.elements.length} items</span>
                        </div>
                        {tpl.layout && (
                          <div className="text-[10px] text-zinc-400">
                            Grid: {tpl.layout.rows}x{tpl.layout.cols} ({tpl.layout.pageSize})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions Footer */}
                  <div className="pt-2 border-t border-zinc-800 flex items-center justify-between gap-2 mt-2">
                    <button
                      onClick={() => handleLoad(tpl)}
                      className="px-3.5 py-1.5 bg-[#1473E6] hover:bg-blue-700 text-white text-xs font-semibold rounded-md shadow-2xs flex items-center gap-1.5 transition-colors flex-1 justify-center"
                    >
                      <Check className="w-3.5 h-3.5" /> Load Template
                    </button>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDuplicate(tpl)}
                        className="p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-md transition-colors"
                        title="Duplicate Template"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => exportTemplateAsJSON(tpl)}
                        className="p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-md transition-colors"
                        title="Export as JSON"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {!tpl.isPreset && (
                        <button
                          onClick={() => handleDelete(tpl.id, tpl.name)}
                          className={`p-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1 ${
                            deletingId === tpl.id
                              ? 'bg-red-600 text-white px-2 py-1 border border-red-500 animate-pulse'
                              : 'text-red-500 hover:text-red-400 hover:bg-red-950/30'
                          }`}
                          title={deletingId === tpl.id ? "Click again to confirm delete" : "Delete Template"}
                        >
                          <Trash2 className="w-4 h-4" />
                          {deletingId === tpl.id && <span>Confirm?</span>}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-900 flex items-center justify-between">
          <span className="text-xs text-zinc-500">
            Loaded templates automatically update your canvas and print layout settings.
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold rounded-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </MacWindow>
  );
};

